import React, { useContext, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FaCalculator, FaCheck, FaInfoCircle, FaTimes } from "react-icons/fa";
import ActionButton from "components/ui/controls/action-button/ActionButton";
import "./DetailPoints.css";

export const DetailPointsContext = React.createContext({});

const formatNombre = (valeur, precision = 6) => Number(valeur).toLocaleString("fr-FR", { maximumFractionDigits: precision });
const afficherAssiette = (detail) => {
  if (detail.Classe !== null && detail.Classe !== undefined && detail.Classe !== "") return `Classe ${detail.Classe}`;
  const assiette = detail.Assiette;
  if (assiette === null || assiette === undefined) return "—";
  if (typeof assiette === "object") return Object.entries(assiette).map(([libelle, montant]) => (
    <span className="detail-points-assiette-ligne" key={libelle}>{libelle} : <span className="detail-points-montant">{formatNombre(montant, 2)} €</span></span>
  ));
  return typeof assiette === "number" ? `${formatNombre(assiette, 2)} €` : assiette;
};

const DetailPoints = ({ caisse, regime = "Complementaire", champ = "PointCalcule", libelle, unite = "points" }) => {
  const resultRetraite = useContext(DetailPointsContext);
  const boutonInfo = useRef(null);
  const [ouvert, setOuvert] = useState(false);
  const infos = resultRetraite?.InfosRetraite?.[caisse];
  const annees = Object.entries(infos?.DetailCalculPoints?.[regime] || {}).sort(([a], [b]) => Number(a) - Number(b));
  const total = annees.reduce((somme, [, details]) => somme + details.reduce((sousTotal, detail) => sousTotal + Number(detail.Resultat), 0), 0);
  const projection = regime === "Repartition" ? total : infos?.General?.[champ];
  const titre = `Détail du calcul — ${libelle.replace("CARCDSF2", "CARCSF")}`;
  const formatResultat = (valeur) => formatNombre(valeur, unite === "points" || regime === "Capitalisation" ? 2 : 6);

  return (
    <>
      <button ref={boutonInfo} type="button" className="detail-points-info" aria-label={`Détail du calcul des points projetés ${libelle}`} title={titre} onClick={() => setOuvert(true)}>
        <FaInfoCircle aria-hidden="true" />
      </button>
      {/* Les modales de l’application utilisent les niveaux 10000 et 10001. */}
      <Dialog
        open={ouvert}
        onClose={() => setOuvert(false)}
        maxWidth="sm"
        fullWidth
        className="detail-points-dialog"
        aria-label={titre}
        sx={{ zIndex: 10002 }}
        // Le portail reste dans le thème actif, au-dessus de la modale de paramétrage.
        container={() => boutonInfo.current?.closest(".App") || document.body}
      >
        <DialogTitle className="detail-points-header">
          <FaCalculator aria-hidden="true" />
          <span>{titre}</span>
          <button type="button" className="detail-points-close" aria-label="Fermer le détail" onClick={() => setOuvert(false)}>
            <FaTimes aria-hidden="true" />
          </button>
        </DialogTitle>
        <DialogContent className="detail-points-content">
          {annees.length === 0 ? (
            <p className="detail-points-empty">Aucun point projeté calculé pour cette caisse sur la période sélectionnée.</p>
          ) : (
            <>
              {regime === "Capitalisation" && <p className="detail-points-description">Chaque résultat correspond au capital atteint à cette étape, en euros.</p>}
              <div className="detail-points-table-container">
                <table className="detail-points-table">
                  <colgroup><col className="detail-points-col-annee" /><col className="detail-points-col-assiette" /><col className="detail-points-col-points" /></colgroup>
                  <thead>
                    <tr><th scope="col">Années</th><th scope="col">Assiette</th><th scope="col">Points</th></tr>
                  </thead>
                  <tbody>
                    {annees.map(([annee, details]) => (
                      <React.Fragment key={annee}>
                        {details.map((detail, index) => (
                          <tr key={`${annee}-${index}`}>
                            <th scope="row">{annee}</th>
                            <td className="detail-points-assiette">{afficherAssiette(detail)}</td>
                            <td className="detail-points-resultat">{formatResultat(detail.Resultat)} {unite}</td>
                          </tr>
                        ))}
                        {details.length > 1 && regime !== "Capitalisation" && (
                          <tr className="detail-points-total"><th scope="row" colSpan={2}>Total {annee}</th><td className="detail-points-resultat">{formatResultat(details.reduce((somme, detail) => somme + Number(detail.Resultat), 0))} {unite}</td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  {regime !== "Capitalisation" && <tfoot><tr><th scope="row" colSpan={2}>Total calculé avant l’arrondi final</th><td className="detail-points-resultat">{formatResultat(total)} {unite}</td></tr></tfoot>}
                </table>
              </div>
              {projection !== undefined && <p className="detail-points-projection"><strong>Total projeté : {formatResultat(projection)} {unite}</strong></p>}
            </>
          )}
        </DialogContent>
        <DialogActions className="detail-points-actions">
          <ActionButton type="button" icon={FaCheck} title="Fermer" onClick={() => setOuvert(false)} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DetailPoints;
